/**
 * **Roll replacements** (`event-engine-to-hit` R10, `decisions/0212` item 2): the `replacement`
 * clauses that rewrite an attack or damage roll before its dice are drawn. Spotter's
 * Particularize turns the d20 into two, keep the higher (`advantage`); Brutal's Predator draws
 * nothing and shows every die's top face (`maximize`).
 *
 * {@link rollReplacements} gathers the clauses that reach the roller (`gatherRollReplacements`),
 * keeps those whose `match` and `condition` hold against the roll's facts, puts them in the F2a
 * order (the roller's own first, then everyone else's by initiative, then gather order) and turns
 * each into a loop {@link Replacement}. The loop applies each at most once.
 */
import { INVISIBLE_STATUS, resourceCapRawKey, type EncounterState } from "@lancer-vtt/domain";
import { gatherAttemptReplacements, gatherRollReplacements, type BoundRollReplacement, type LoadedOverlay } from "../effects/loader";
import { evaluateMatch } from "../effects/family";
import { evaluatePredicate, type PredicateContext } from "../effects/predicate";
import type { RollReplacementEffect } from "../effects/atoms";
import { affordable, emitEvent, replacementOrder } from "./gameEvents";
import type { DiceSource } from "../../dice";
import type { EventLoop, KernelEvent, Replacement } from "./loop";
import { drawRoll } from "./rollEvents";

/** Named args for {@link rollReplacements}. */
export type RollReplacementsArgs = {
  state: EncounterState;
  overlay: LoadedOverlay | undefined;
  roller: string;
  roll: RollReplacementEffect["roll"];
  /** The firing weapon, whose tags carry their own clauses. */
  weapon?: { tags?: { id: string; val?: number }[] };
  /** The roller's bound Grappled partner, as the attacker-side modifier fold threads it. */
  partner?: string;
  /** The attack's facts the clauses match on: pre-roll for the attack roll, plus the outcome for a damage roll. */
  facts: PredicateContext;
};

/** The replacements on one roll, in the order the loop applies them. Empty without an overlay, or when it holds no roll replacement. */
export function rollReplacements(args: RollReplacementsArgs): Replacement[] {
  const { state, overlay, roller, roll, weapon, partner, facts } = args;
  if (overlay === undefined || !carriesRollReplacements(overlay)) return [];
  const live = gatherRollReplacements({ state, rollerId: roller, weapon, overlay, ...(partner !== undefined ? { partner } : {}) }).filter((r) => r.clause.roll === roll && holds(r, facts));
  return replacementOrder(live, roller, state).map((r) => ({
    id: `${r.entityId}:${r.clause.name}`,
    replace: (event) => {
      const rewritten = rewrite(event, r.clause.instead);
      const cap = r.clause.consumeResourceOnApply;
      if (cap !== undefined && rewritten !== event) emitEvent(facts.costs!, { kind: "resource_cap_use", carrier: r.carrier, key: resourceCapRawKey(cap) });
      return rewritten;
    },
  }));
}

/** Overlays with no roll replacement in them, the usual case: the gather is skipped for them. */
const rollClauses = new WeakMap<LoadedOverlay, boolean>();

function carriesRollReplacements(overlay: LoadedOverlay): boolean {
  let carries = rollClauses.get(overlay);
  if (carries === undefined) rollClauses.set(overlay, (carries = Object.values(overlay).some((entry) => entry.effects.some((e) => e.kind === "replacement" && e.on === "roll"))));
  return carries;
}

/** One miss-chance clause that holds on an attack: the entity that carries it and its chance. */
export type MissChance = { source: string; chance: number };

/** Named args for {@link attemptMissChances}. */
export type AttemptMissChancesArgs = {
  state: EncounterState;
  overlay: LoadedOverlay | undefined;
  target: string;
  /** The attack's facts, `self` the attacker, as the target-side modifier fold reads them. */
  facts: PredicateContext;
  /** A Lotus Projector's field denies the target Invisible (`deny_invisible`): Invisible's own clause drops. */
  invisibleDenied?: boolean;
};

/**
 * The miss-chance clauses that hold on an attack against `target` (`event-engine-to-hit` 3.1), in
 * the F2a order (the target's own first). Pure: the forecast reads the chance from here, and
 * {@link missChanceReplacements} turns them into the attempt's replacements.
 */
export function attemptMissChances(args: AttemptMissChancesArgs): MissChance[] {
  const { state, overlay, target, facts, invisibleDenied } = args;
  if (overlay === undefined) return [];
  const live = gatherAttemptReplacements(state, target, overlay).filter((r) => {
    if (invisibleDenied === true && r.entityId === INVISIBLE_STATUS) return false;
    const ctx: PredicateContext = r.partner !== undefined ? { ...facts, actors: { ...facts.actors, partner: r.partner } } : facts;
    return r.clause.condition === undefined || evaluatePredicate(r.clause.condition, ctx);
  });
  return replacementOrder(live, target, state).map((r) => ({ source: r.entityId, chance: r.clause.instead.chance }));
}

/**
 * The attempt's miss-chance replacements: the first draws one coin (a d2, through the loop's dice
 * as a `roll`), and its face decides the attempt; a later one finds the coin drawn and changes
 * nothing, so an attempt never draws two.
 */
export function missChanceReplacements(chances: readonly MissChance[], at: { loop: EventLoop; dice: DiceSource; attacker: string; target: string }): Replacement[] {
  return chances.map(({ source, chance }) => ({
    id: `${source}:miss_chance`,
    replace: (event) => {
      if (event.kind !== "attack_attempt" || event.coin !== undefined) return event;
      const face = drawRoll({
        loop: at.loop,
        dice: at.dice,
        roll: { kind: "roll", roller: at.attacker, purpose: { kind: "miss_chance", target: at.target }, spec: { dice: [{ count: 1, sides: 2 }], accuracy: 0 } },
        draw: (d) => d.roll(2),
      }).result;
      return { ...event, coin: { face, chance } };
    },
  }));
}

/** Whether a gathered clause applies to this roll. A conferred clause reads its granting ally as `partner`. */
function holds(r: BoundRollReplacement, facts: PredicateContext): boolean {
  // A capped clause applies only where its use can be spent (the action's ledger) and while the cap has room.
  const cap = r.clause.consumeResourceOnApply;
  if (cap !== undefined && (facts.costs === undefined || !affordable({ kind: "resource_cap_use", carrier: r.carrier, key: resourceCapRawKey(cap), max: cap.max }, facts.state, facts.costs))) return false;
  const ctx: PredicateContext = r.partner !== undefined ? { ...facts, actors: { ...facts.actors, partner: r.partner } } : facts;
  if (r.clause.match !== undefined && !evaluateMatch(r.clause.match, ctx.event)) return false;
  return r.clause.condition === undefined || evaluatePredicate(r.clause.condition, ctx);
}

/** Rewrite a proposed roll's spec. Advantage doubles the first pool's single die and keeps the higher; a second source changes nothing. */
function rewrite(event: KernelEvent, instead: RollReplacementEffect["instead"]): KernelEvent {
  if (event.kind !== "roll") return event;
  if (instead.op === "maximize") return { ...event, spec: { ...event.spec, maximize: true } };
  const [first, ...rest] = event.spec.dice;
  if (first === undefined || event.spec.keepHighest !== undefined) return event;
  return { ...event, spec: { ...event.spec, dice: [{ ...first, count: first.count * 2 }, ...rest], keepHighest: first.count } };
}
